import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import styled from 'styled-components';

import Avatar from '@components/dataDisplay/Avatar';
import {
  currentUserAuthTypeState,
  currentUserIDState,
  isMasterState,
} from '@atom/setting/user';
import { useDocument } from '@nandorojo/swr-firestore';
import { User } from '@typings/setting/userType.interface';
import { userLogout } from '@store/firebase/user/registerUser';
import DropdownWrapper, {
  dropdownDirMap,
} from '@components/wrapper/DropdownWrapper';
import { RootPath } from '@routes/routePath';
import { AnimatePresence } from 'framer-motion';
import Icon from '@components/dataDisplay/Icons/Icon';
import { AUTH_TYPE, AUTH_TYPE_MAP } from '@typings/common/authorizationType';
import { cookieStore } from '@store/cookie/cookieStore';
import { CollectionPath } from '@typings/collections.enum';
import { ISoundEffect } from '@typings/setting/soundEffect.interface';
import AccountManageModal from '@pages/Register/Accounts/AccountManageModal';
import AccountList from '@pages/Register/Accounts/AccountList';
import { useCollection } from '@nandorojo/swr-firestore';
import {
  IMemberRole,
  MEMBER_ROLE_COLLECTION,
} from '@typings/setting/memberRole.interface';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DisplaySec = styled.div`
  padding: 16px 8px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  font-size: 17px;
  font-weight: 800;
  word-break: keep-all;
  gap: 8px;
`;

const UserSecWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
  }
`;

export const Tag = styled.div`
  display: inline-block;
  font-size: 0.7rem;
  padding: 4px 6px;
  border-radius: var(--buttonBorderRadius);
  background-color: var(--subColor);
  color: var(--subTextColor);
`;

const MenuBtn = styled.button`
  display: inherit;
  padding: 6px 4px;
  width: 100%;
  text-align: left;
  display: flex;
  gap: 6px;
  align-items: center;
  border-radius: var(--cardBorderRadius);
  font-family: var(--defaultFontFamily);
  color: var(--textColor);

  &:hover {
    background-color: var(--inkColor);
  }
`;

export const LogoutBtn = styled.button`
  display: inherit;
  padding: 8px;
  width: 100%;
  border-radius: var(--cardBorderRadius);
  font-family: var(--defaultFontFamily);
  color: var(--subTextColor);
  font-size: 13px;

  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    font-weight: 700;
  }
`;

const DisplayName = styled.div`
  color: var(--menuTextColor);
  line-height: 1.2;
  /* ellipsis */
  max-width: 100px;
  max-height: 40px;
  overflow: hidden;
  white-space: normal;
  text-overflow: ellipsis;
  word-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  /* ellipsis end */
`;

const MasterSec = styled.div`
  border-top: 1px solid var(--lineColor);
  border-bottom: 1px solid var(--lineColor);
  padding: 6px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

type UserSecProps = {
  dropdownDir?: keyof typeof dropdownDirMap;
  showDisplayName?: boolean;
  close?: () => void;
  size?: string;
  soundEff?: ISoundEffect | any;
  style?: any;
};

function UserSec({
  dropdownDir,
  showDisplayName,
  close,
  size,
  soundEff = {},
  style,
}: UserSecProps) {
  const setCurrentUid = useSetRecoilState(currentUserIDState);
  const currentUserId = useRecoilValue(currentUserIDState); // Recoil 상태 사용 (반응형)
  const cacheUserId = cookieStore.getUserToken();
  const setCurrentUserAuthType = useSetRecoilState(currentUserAuthTypeState);

  const [openAccountModal, setAccountOpenModal] = useState(false);
  const history = useNavigate();
  const targetRef = useRef<HTMLDivElement>(null);
  const [isUserOpen, setIsUserOpen] = useState(false);
  const isMaster = useRecoilValue(isMasterState);

  const handleToggleUserDropdown = () => {
    setIsUserOpen(!isUserOpen);
  };
  const { data: userData, error } = useDocument<User>(
    `${CollectionPath.USERS}/${currentUserId}`,
    { listen: true },
  );

  // 🆕 멤버 역할 정보 가져오기
  const { data: roleList } = useCollection<IMemberRole>(
    MEMBER_ROLE_COLLECTION,
    {
      listen: true,
    },
  );

  // 🆕 현재 사용자의 역할 찾기
  const userRole = roleList?.find((role) =>
    role.userIds?.includes(currentUserId || ''),
  );

  // 🆕 멤버이면서 역할이 있는 경우
  const isMemberWithRole =
    userData?.authorizationType === AUTH_TYPE.MEMBER && !!userRole;

  async function logout() {
    // 1. Recoil State 먼저 초기화
    setCurrentUid(null);
    setCurrentUserAuthType(AUTH_TYPE.GUEST);
    setIsUserOpen(false);

    // 2. Firebase 로그아웃 및 쿠키 삭제 (signOut 완료 후 리다이렉트)
    await userLogout();
  }

  useEffect(() => {
    if (currentUserId && userData && cacheUserId) {
      setCurrentUserAuthType(userData.authorizationType);
    } else {
      setCurrentUserAuthType(AUTH_TYPE.GUEST);
    }
  }, [currentUserId, userData, cacheUserId]);

  function handleClickOutside(e: any, dropdownNode) {
    if (targetRef.current && isUserOpen && dropdownNode) {
      const isClickOutside =
        !targetRef.current.contains(e.target) &&
        !dropdownNode.contains(e.target);

      if (isClickOutside) {
        setIsUserOpen(false);
      }
    }
  }

  function handleClickUserMenu(pathname) {
    history(pathname);
    if (close) close();
    setIsUserOpen(false);
  }

  return (
    <>
      <UserSecWrapper
        ref={targetRef}
        onClick={handleToggleUserDropdown}
        style={style}
      >
        <Avatar
          photoURL={userData ? userData.photoURL : ''}
          size={size || '32px'}
          round
        />
        {showDisplayName && <DisplayName>{userData?.displayName}</DisplayName>}

        {openAccountModal && (
          <AccountManageModal setModal={setAccountOpenModal} />
        )}

        <AnimatePresence>
          {isUserOpen && (
            <DropdownWrapper
              open={isUserOpen}
              direction={dropdownDir}
              handleClickOutside={handleClickOutside}
              style={{ width: 160 }}
            >
              <Container>
                <DisplaySec>
                  <Tag>{AUTH_TYPE_MAP[userData!.authorizationType]}</Tag>
                  {userRole && (
                    <Tag
                      style={{
                        background: 'var(--primaryColor)',
                        color: 'white',
                      }}
                    >
                      {userRole.label}
                    </Tag>
                  )}
                  <div>{userData && userData.displayName}</div>
                </DisplaySec>
                <div>
                  <MenuBtn
                    onClick={() =>
                      handleClickUserMenu(
                        `${RootPath.User}/${currentUserId}/${RootPath.UserDetail}`,
                      )
                    }
                  >
                    <Icon name="Person14" /> 내 계정
                  </MenuBtn>
                  <MenuBtn
                    onClick={() =>
                      handleClickUserMenu(
                        `/${RootPath.MyPageSetting}/${RootPath.MyPageNotification}`,
                      )
                    }
                  >
                    <Icon name="Wheel18" /> 설정
                  </MenuBtn>
                  {(isMaster || isMemberWithRole) && (
                    <>
                      <MenuBtn
                        onClick={() =>
                          handleClickUserMenu(
                            `/${RootPath.Setting}/${RootPath.Homepage}`,
                          )
                        }
                      >
                        <Icon name="Wheel18" />
                        홈페이지 설정
                      </MenuBtn>
                    </>
                  )}
                </div>
                <MasterSec>
                  <MenuBtn onClick={() => setAccountOpenModal(true)}>
                    <Icon name="EarthBorn14" /> 계정 관리
                  </MenuBtn>
                  <AccountList shape="token" />
                </MasterSec>

                <LogoutBtn onClick={logout}>
                  <Icon name="On14" color="var(--subTextColor)" />
                  로그아웃
                </LogoutBtn>
              </Container>
            </DropdownWrapper>
          )}
        </AnimatePresence>
      </UserSecWrapper>
    </>
  );
}

export default UserSec;
